import { hash } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import type { Dayjs } from 'dayjs';
import { notEq } from 'ember-truth-helpers';
import MarketTotalRevenueElegantDateHandlingBanner from 'lumen/components/market/total-revenue/elegant-date-handling/banner';
import MarketTotalRevenueElegantDateHandlingModal from 'lumen/components/market/total-revenue/elegant-date-handling/modal';
import type { Reference } from 'lumen/components/market/total-revenue/reference-picker';
import type { TotalRevenueHotel } from 'lumen/models';
import { getReferencePeriod } from 'lumen/utils/reference-period';

export interface DateRange {
  from: Dayjs;
  to: Dayjs;
}

export interface PropertyWithIncompleteData {
  periodsWithoutData: DateRange[];
  property: TotalRevenueHotel;
}

export interface PeriodWithPropertiesWithIncompleteData {
  id: string;
  periodTranslation: string;
  properties: PropertyWithIncompleteData[];
}

interface MarketTotalRevenueElegantDateHandlingSignature {
  Args: {
    currentPeriod: DateRange;
    properties: TotalRevenueHotel[];
    referencePeriods: Reference[];
  };
  Blocks: {
    default: [
      {
        Banner: WithBoundArgs<
          typeof MarketTotalRevenueElegantDateHandlingBanner,
          | 'nrOfProperties'
          | 'nrOfPropertiesWithCompleteData'
          | 'periodsWithPropertiesWithIncompleteData'
        >;
        Modal: WithBoundArgs<
          typeof MarketTotalRevenueElegantDateHandlingModal,
          | 'periodsWithPropertiesWithIncompleteData'
          | 'canRemovePropertiesFromFilter'
        >;
        hasPropertiesWithPartialDataInCurrentPeriod: boolean;
        hasPropertiesWithPartialDataInReferencePeriod: boolean;
      },
    ];
  };
}

export default class MarketTotalRevenueElegantDateHandling extends Component<MarketTotalRevenueElegantDateHandlingSignature> {
  @service declare intl: Services['intl'];
  @service declare totalRevenue: Services['total-revenue'];

  get currentPeriodWithPropertiesWithIncompleteData(): PeriodWithPropertiesWithIncompleteData {
    const { currentPeriod } = this.args;

    return {
      id: 'current',
      periodTranslation: this.intl.t('general.current_period'),
      properties: this.getPropertiesWithIncompleteDataPeriods(
        currentPeriod.from,
        currentPeriod.to,
      ),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPropertiesWithPartialDataInCurrentPeriod() {
    return (
      this.currentPeriodWithPropertiesWithIncompleteData.properties.length > 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPropertiesWithPartialDataInReferencePeriod() {
    return this.referencePeriodsWithPropertiesWithIncompleteData.some(
      ({ properties }) => properties.length > 0,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nrOfPropertiesWithCompleteData() {
    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfPropertiesWithCompleteData() {
    const propertiesWithIncompleteData = this.args.properties.filter(
      (property) => {
        return [this.args.currentPeriod, ...this.args.referencePeriods].some(
          (period) => {
            if ('to' in period) {
              return (
                property.getPeriodsWithoutData(period.from, period.to).length >
                0
              );
            }

            const range = this.getDateRangeForReferencePeriod(period);
            return (
              property.getPeriodsWithoutData(range.from, range.to).length > 0
            );
          },
        );
      },
    );

    return this.args.properties.length - propertiesWithIncompleteData.length;
  }

  get periodsWithPropertiesWithIncompleteData(): PeriodWithPropertiesWithIncompleteData[] {
    return [
      this.currentPeriodWithPropertiesWithIncompleteData,
      ...this.referencePeriodsWithPropertiesWithIncompleteData,
    ].filter(({ properties }) => properties.length > 0);
  }

  get referencePeriodsWithPropertiesWithIncompleteData(): PeriodWithPropertiesWithIncompleteData[] {
    return this.args.referencePeriods.map((referencePeriod) => {
      const { from, to } = this.getDateRangeForReferencePeriod(referencePeriod);

      return {
        id: referencePeriod.id,
        periodTranslation: this.totalRevenue.translateReferenceType(
          referencePeriod.id,
        ),
        properties: this.getPropertiesWithIncompleteDataPeriods(from, to),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getDateRangeForReferencePeriod(referencePeriod: Reference) {
    const { currentPeriod } = this.args;

    if (referencePeriod.id === 'custom') {
      const numberOfDays = currentPeriod.to.diff(currentPeriod.from, 'day');
      return {
        from: referencePeriod.fromDate,
        to: referencePeriod.fromDate.add(numberOfDays, 'day'),
      };
    }

    const range = getReferencePeriod(
      currentPeriod.from,
      currentPeriod.to,
      referencePeriod.id,
    );
    return {
      from: range.start,
      to: range.end,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getPropertiesWithIncompleteDataPeriods(fromDate: Dayjs, toDate: Dayjs) {
    return this.args.properties
      .map((property) => ({
        property,
        periodsWithoutData: property.getPeriodsWithoutData(fromDate, toDate),
      }))
      .filter(({ periodsWithoutData }) => periodsWithoutData.length > 0);
  }

  <template>
    {{yield
      (hash
        Banner=(component
          MarketTotalRevenueElegantDateHandlingBanner
          nrOfProperties=@properties.length
          nrOfPropertiesWithCompleteData=this.numberOfPropertiesWithCompleteData
          periodsWithPropertiesWithIncompleteData=this.periodsWithPropertiesWithIncompleteData
        )
        Modal=(component
          MarketTotalRevenueElegantDateHandlingModal
          canRemovePropertiesFromFilter=(notEq
            this.numberOfPropertiesWithCompleteData 0
          )
          periodsWithPropertiesWithIncompleteData=this.periodsWithPropertiesWithIncompleteData
        )
        hasPropertiesWithPartialDataInCurrentPeriod=this.hasPropertiesWithPartialDataInCurrentPeriod
        hasPropertiesWithPartialDataInReferencePeriod=this.hasPropertiesWithPartialDataInReferencePeriod
      )
    }}
  </template>
}
